import React, { useState, useEffect, useCallback } from "react";

import { Plus } from "icons";

import domainsExploreApi from "apis/domainsExplore";
import { Button, PageLoader, Typography } from "clearsense-ui/components";
import ExploreContainer from "components/Common/ExploreContainer";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { useUserState } from "contexts/user";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import { getPermissions } from "utils/roleBasedAccess";

import AssociateReferenceSources from "./AssociateReferenceSources";
import DeleteReferenceSource from "./DeleteReferenceSource";
import ReferenceSourcesTable from "./ReferenceSourceTable";

import { REFERENCE_SOURCES_DATA } from "../../constants/referenceSource";
import DomainsSummaryCard from "../DomainsSummaryCard";

const ReferenceSources = ({ domain, features, getDomain }) => {
  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);

  const [isLoading, setIsLoading] = useState(false);
  const [referenceSources, setReferenceSources] = useState({});
  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});

  const columnHeaderSelector = useColumnHeaderSelector(
    REFERENCE_SOURCES_DATA()
  );

  const fetchReferenceSources = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 5;
      const { data } = await domainsExploreApi.fetchReferenceSources(
        domain.id,
        currentPage,
        pageSize,
        search
      );
      setReferenceSources({
        data: data?.reference_sources?.reference_sources,
        count: data?.reference_sources?.reference_sources_count,
      });
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferenceSources();
  }, []);

  const filterParams = useSearchAndPaginate(fetchReferenceSources, {
    all: () => setCheckedRows([]),
  });

  return (
    <>
      <ExploreContainer>
        <div className="flex justify-between">
          <Typography style="h3" type="semibold">
            Reference Source using {domain.name}({referenceSources?.count})
          </Typography>
          <Typography style="h3" type="semibold">
            Summary
          </Typography>
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="associate"
          >
            <Button
              label="Associate Reference Sources"
              icon={Plus}
              onClick={() => setIsPaneOpen(true)}
              data-cy="associate-reference-source-button"
            />
          </PermissibleWrapper>
        </div>
        <DomainsSummaryCard domain={domain} />
        <FilterHeader
          entity="Reference Source"
          filterParams={filterParams}
          columnHeaderSelector={columnHeaderSelector}
          count={referenceSources?.count}
          checkedRows={checkedRows}
          setIsDeleteAlertOpen={setIsDeleteAlertOpen}
          userPermissions={userPermissions}
        />
        {isLoading ? (
          <PageLoader />
        ) : (
          <ReferenceSourcesTable
            setIsPaneOpen={setIsPaneOpen}
            userPermissions={userPermissions}
            referenceSources={referenceSources?.data}
            referenceSourcesCount={referenceSources?.count}
            columns={columnHeaderSelector.columns}
            setCheckedRows={setCheckedRows}
            checkedRows={checkedRows}
            filterParams={filterParams}
          />
        )}
      </ExploreContainer>
      <AssociateReferenceSources
        isPaneOpen={isPaneOpen}
        setIsPaneOpen={setIsPaneOpen}
        domain={domain}
        getDomain={getDomain}
        getReferenceSources={fetchReferenceSources}
      />
      <DeleteReferenceSource
        rowToBeDeleted={rowToBeDeleted}
        checkedRows={checkedRows}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        setRowToBeDeleted={setRowToBeDeleted}
      />
    </>
  );
};

export default ReferenceSources;
